import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import type { SubagentEvent } from "./useSubagentEvents";

interface SubagentActivityProps {
  events: SubagentEvent[];
}

// A result is an error only when the summary explicitly says so. Everything
// else (JSON data, skill content, etc.) counts as success.
function isError(summary: string | undefined): boolean {
  if (!summary) return false;
  const s = summary.toLowerCase();
  return (
    s.startsWith("error") ||
    s.startsWith("there was an error") ||
    s.startsWith("failed") ||
    s.startsWith("traceback")
  );
}

const TOOL_LABELS: Record<string, string> = {
  execute_code: "Executing code",
  execute_sql: "Running SQL",
};

function getStepLabel(evt: SubagentEvent): string {
  if (evt.kind === "tool_call") {
    return TOOL_LABELS[evt.toolName] ?? `Running ${evt.toolName}`;
  }
  return evt.summary?.split("\n")[0] ?? "Done";
}

// Pair each tool_call with its tool_result by arrival order (FIFO). All
// subagent events under one parent tool call share the same toolCallId, so
// positional pairing is the only reliable strategy.
function buildCallResultPairs(events: SubagentEvent[]): Map<number, SubagentEvent> {
  const pairs = new Map<number, SubagentEvent>();
  const unresolved: number[] = [];
  events.forEach((evt, i) => {
    if (evt.kind === "tool_call") {
      unresolved.push(i);
    } else if (evt.kind === "tool_result" && unresolved.length > 0) {
      pairs.set(unresolved.shift()!, evt);
    }
  });
  return pairs;
}

export const SubagentActivity = ({ events }: SubagentActivityProps) => {
  if (events.length === 0) return null;
  const pairs = buildCallResultPairs(events);
  return (
    <div className="space-y-1 px-3 py-2">
      {events.map((evt, i) => (
        <div key={i} className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <StepIcon evt={evt} resolvedResult={pairs.get(i)} />
          <span className="min-w-0 break-words leading-relaxed">{getStepLabel(evt)}</span>
        </div>
      ))}
    </div>
  );
};

interface StepIconProps {
  evt: SubagentEvent;
  resolvedResult?: SubagentEvent;
}

const StepIcon = ({ evt, resolvedResult }: StepIconProps) => {
  if (evt.kind === "tool_call") {
    if (!resolvedResult) {
      return <Loader2 className="mt-0.5 size-3 shrink-0 animate-spin text-muted-foreground" />;
    }
    return isError(resolvedResult.summary) ? (
      <XCircle className="mt-0.5 size-3 shrink-0 text-red-400" />
    ) : (
      <CheckCircle2 className="mt-0.5 size-3 shrink-0 text-green-500" />
    );
  }
  return isError(evt.summary) ? (
    <XCircle className="mt-0.5 size-3 shrink-0 text-red-400" />
  ) : (
    <CheckCircle2 className="mt-0.5 size-3 shrink-0 text-green-500" />
  );
};
